import debounce from 'lodash-es/debounce';
import { useEffect, useState } from 'react';

const DEBOUNCE_TIMEOUT: number = 300;

/**
 * Function which handles a promise call and returns the data
 * @param initialData Initial data for the hook
 * @param shouldDebounce debounce() should be applied or not
 * @param dataFetchingPromise The promise which will call the api to fetch the data
 * @param dependencies array of dependencies, which on change, will fire fetch calls
 * @param args Arguments required for the promise
 */
export const useDataFetchState = <T>(
    initialData: T,
    shouldDebounce: boolean,
    dataFetchingPromise: (...args: any[]) => Promise<T>,
    dependencies: any[],
    ...args
) => {
    const [data, setData] = useState(initialData);
    const [count, setCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    useEffect(() => {
        let componentUnmounted: boolean = false;
        const fetchData = async () => {
            setIsError(false);
            setIsLoading(true);
            try {
                const result = await dataFetchingPromise(...args);
                !componentUnmounted ? setData(result) : null;
            } catch (error) {
                !componentUnmounted ? setIsError(true) : null;
            } finally {
                !componentUnmounted ? setIsLoading(false) : null;
            }
        };
        fetchData();

        /**
         * Cleanup function to implement componentDidUnMount logic
         */
        return () => (componentUnmounted = true);
    }, [...dependencies, count]);

    const updateCount = () => {
        setCount(count => count + 1);
    };

    const dataFetch = shouldDebounce ? debounce(updateCount, DEBOUNCE_TIMEOUT) : updateCount;

    return { data, isLoading, isError, dataFetch };
};
